import React, { createContext, useSyncExternalStore } from "react"
import { LoadPrecedence, SystemNode, SystemViewData } from "../types/SystemView"

/**
 * Load Precedence store: each node's latest verdict, keyed by the node's Asset id.
 * Refreshed on the initial load and every poll, like node alerts, so the rows on
 * a node follow the latest bucket without the ReactFlow canvas re-rendering.
 */
type LoadPrecedenceMap = Record<string, LoadPrecedence>

export function createLoadPrecedenceStore() {
  let current: LoadPrecedenceMap = {}
  const listeners = new Set<() => void>()

  return {
    get: () => current,
    set: (values: LoadPrecedenceMap) => {
      current = values
      listeners.forEach((l) => l())
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

export const LoadPrecedenceStoreContext = createContext<ReturnType<typeof createLoadPrecedenceStore> | null>(null)

/** Push every node's Load Precedence from a getSystemView response to the store. */
export function pushLoadPrecedence(dataView: SystemViewData, store: ReturnType<typeof createLoadPrecedenceStore>) {
  const values: LoadPrecedenceMap = {}
  const allNodes: SystemNode[] = [...dataView.installations.flatMap((g) => g.nodes), ...dataView.isolatedNodes]
  for (const node of allNodes) {
    const assetId = node.asset?.id
    if (!assetId || !node.loadPrecedence) continue
    values[assetId] = node.loadPrecedence
  }
  store.set(values)
}

/** The latest Load Precedence for a node's Asset; re-renders only the subscribing rows. */
export function useNodeLoadPrecedence(assetId: string | undefined): LoadPrecedence | undefined {
  const store = React.useContext(LoadPrecedenceStoreContext)

  const subscribe = React.useCallback(
    (onStoreChange: () => void) => {
      if (!store) return () => {}
      return store.subscribe(onStoreChange)
    },
    [store],
  )

  const getSnapshot = React.useCallback(() => {
    if (!store || !assetId) return undefined
    return store.get()[assetId]
  }, [store, assetId])

  return useSyncExternalStore(subscribe, getSnapshot)
}
